import React from 'react';

import { bemBlock, Button, Icon } from '@neptune/shared/venus-ui';

import { useHold } from 'common/hooks/useHold';
import LoadingIndicator from 'components/loading-indicator/LoadingIndicator';

import './CopyToClipboard.less';

const NOTIFICATION_TIMEOUT = 2000;

type CopyToClipboardProps = {
  className?: string;
  text?: string;
  getText?: () => Promise<string | undefined>;
  title?: string;
  size?: React.ComponentProps<typeof Button>['size'];
  variant?: React.ComponentProps<typeof Button>['variant'];
  children?: React.ReactNode;
};

const block = bemBlock('copy-to-clipboard');

export const CopyToClipboard = ({
  className,
  text,
  getText,
  size,
  variant = 'transparent',
  title = 'Copy to clipboard',
  children,
}: CopyToClipboardProps) => {
  const [loading, setLoading] = React.useState(false);
  const { activate, active: isNotificationActive } = useHold(NOTIFICATION_TIMEOUT);

  const handleCopy = React.useCallback(async () => {
    setLoading(true);
    const textToClipboard = text === undefined && getText ? await getText() : text;

    if (!textToClipboard) {
      setLoading(false);
      return;
    }

    await navigator.clipboard.writeText(textToClipboard);
    setLoading(false);
    activate();
  }, [getText, text, activate]);

  const copyButtonClasses = block({
    extra: className,
    modifiers: { active: isNotificationActive },
  });

  return (
    <Button
      square
      className={copyButtonClasses}
      onClick={handleCopy}
      variant={variant}
      data-heap-redact-attributes="data-clipboard-text"
      data-clipboard-text={text}
      data-role="copy-to-clipboard"
      title={title}
      size={size}
    >
      <LoadingIndicator size="small" isVisible={loading} />
      {!loading ? (
        <Icon className={block('icon')} glyph={isNotificationActive ? 'check' : 'clone'} />
      ) : undefined}
      {children}
    </Button>
  );
};
